import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { api, prayerWallApi } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';
import { useNavDots } from '../context/NavDotContext';
import { useRecipients } from '../context/RecipientsContext';
import {
  CalendarDays, Compass, BookMarked, Plus,
  UserPlus, Users, AlertTriangle,
} from 'lucide-react';
import PageHeader from './PageHeader';
import PageGuide from './PageGuide';
import SectionTabs from './SectionTabs';
import {
  ymd, ym, visibleMonthsForRange,
  AddEditWallModal, Toast,
} from '../screens/PrayerScreen';
import '../screens/PrayerScreen.css';
import '../screens/PrayerWallScreen.css';

// Map route segments to tab keys for header title/subtitle.
// Recipients is gone — the list is the People section now. The word
// "recipients" still belongs here though: the calendar assigns them.
const TAB_MAP = {
  calendar: 'calendar',
  discover: 'discover',
  requests: 'requests',
};

export default function PrayerLayout() {
  const { t } = useTranslation();
  const { dbUser } = useAuth();
  const currentUserId = dbUser?.id;
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshDots } = useNavDots();

  // Recipients live in a provider above both Prayer and People now — the same
  // records are "recipients" to the calendar and "people" to the People
  // section, and both views have to stay in step.
  const {
    recipients, setRecipients, ensureLoaded: ensureRecipientsLoaded,
    reload: reloadRecipients, onRecipientsChanged,
    openAdd, openQuickAdd, deleteRecipient, invite,
  } = useRecipients();

  // Determine active tab from URL
  const segments = location.pathname.replace(/\/+$/, '').split('/');
  const lastSeg = segments[segments.length - 1];
  const activeTab = TAB_MAP[lastSeg] || 'calendar';

  // ── Shared state ───────────────────────────────────────────────
  const [calEvents, setCalEvents]           = useState([]);
  const [wallCalEvents, setWallCalEvents]   = useState([]);
  const [coverPhotos, setCoverPhotos]       = useState({ general: [], memorial: [] });
  const [prayerStats, setPrayerStats]       = useState({ currentStreak: 0, bestStreak: 0 });
  const [wallItems, setWallItems]           = useState([]);
  const [wallLoading, setWallLoading]       = useState(true);
  const [discoverCount, setDiscoverCount]   = useState(0);
  const [deepLinkItemId, setDeepLinkItemId] = useState(null);
  const [loading, setLoading]               = useState(true);
  const [loadError, setLoadError]           = useState('');
  const [toast, setToast]                   = useState('');

  // Modals. The recipient add/edit/quick-add/invite sheets live in
  // RecipientsProvider so People and Prayer open the same dialogs; only the
  // prayer-request sheet is local to this section.
  const [showAddWall, setShowAddWall]       = useState(false);
  const [showAddMenu, setShowAddMenu]       = useState(false);
  const addMenuRef = useRef();

  // Wall edit/answer state (shared with RequestsTab)
  const [wallEditItem, setWallEditItem]     = useState(null);
  const [wallAnswerItem, setWallAnswerItem] = useState(null);

  // Calendar navigation state
  const [viewDate, setViewDate]             = useState(new Date());
  const [selectedDay, setSelectedDay]       = useState(ymd(new Date()));
  const [selectedEvent, setSelectedEvent]   = useState(null);
  const [viewMode, setViewModeRaw]          = useState(() => {
    try { return localStorage.getItem('ignite-prayer-cal-mode') === 'month' ? 'month' : 'week'; }
    catch { return 'week'; }
  });
  const detailRef = useRef();
  function setViewMode(mode) {
    setViewModeRaw(mode);
    try { localStorage.setItem('ignite-prayer-cal-mode', mode); } catch {}
  }

  const MONTHS = t('prayer.months');

  // ── Initial load ───────────────────────────────────────────────
  useEffect(() => {
    loadAll();
    // Handle navigation state from other screens (e.g. CoursesScreen prayer bridge)
    if (location.state?.showAddWall) {
      navigate('/prayer/requests', { replace: true });
      setShowAddWall(true);
      window.history.replaceState({}, '', '/prayer/requests');
    }
    // Handle legacy deep-link query params (?tab=... &itemId=...)
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    const itemId   = params.get('itemId');
    if (tabParam === 'discover')   navigate('/prayer/discover', { replace: true });
    if (tabParam === 'prayingFor') navigate('/prayer/requests?sub=prayingFor', { replace: true });
    if (tabParam === 'wall')       navigate('/prayer/requests', { replace: true });
    if (tabParam === 'requests')   navigate('/prayer/requests', { replace: true });
    if (itemId) setDeepLinkItemId(itemId);
  }, []); // eslint-disable-line

  // Smart default: pick the right tab when the user first lands on bare
  // /prayer — Discover if there is something waiting there and nothing unprayed
  // today, otherwise the calendar.
  //
  // Fires once per mount only. Tapping Prayer in the bottom nav when you are
  // already in Prayer is the "I'm lost" gesture and lands you on /prayer; if
  // this effect kept running it would bounce you straight back to Discover and
  // the escape hatch would not work.
  const smartDefaultDone = useRef(false);
  useEffect(() => {
    if (loading || smartDefaultDone.current) return;
    if (location.pathname !== '/prayer' && location.pathname !== '/prayer/') return;
    smartDefaultDone.current = true;

    const todayStr = ymd(new Date());
    const hasUnprayedToday =
      calEvents.some(e => !e.prayedAt && e.scheduledFor.slice(0, 10) === todayStr) ||
      wallCalEvents.some(e => !e.prayedAt && e.scheduledFor.slice(0, 10) === todayStr);

    if (!hasUnprayedToday && discoverCount > 0) navigate('/prayer/discover', { replace: true });
    else                                        navigate('/prayer/calendar', { replace: true });
  }, [loading, location.pathname, calEvents, wallCalEvents, discoverCount]); // eslint-disable-line

  // Landing on bare /prayer after the smart default has already run (i.e. the
  // reset gesture) still needs a tab — always the default one.
  useEffect(() => {
    if (!smartDefaultDone.current) return;
    if (location.pathname === '/prayer' || location.pathname === '/prayer/') {
      navigate('/prayer/calendar', { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => { setSelectedEvent(null); }, [selectedDay]);
  useEffect(() => {
    if (selectedEvent && detailRef.current) {
      setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
    }
  }, [selectedEvent]);

  // Close add-menu on outside click
  useEffect(() => {
    function handleClick(e) {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) setShowAddMenu(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // ── Data loading ───────────────────────────────────────────────
  async function loadAll() {
    setLoading(true); setLoadError('');
    // Recipients are fetched by the shared provider, not here — kicking it off
    // alongside the rest keeps the same single round of requests on entry.
    const [rResult, cResult, wResult, wallResult, discResult, coverResult, statsResult] = await Promise.allSettled([
      ensureRecipientsLoaded(),
      api.get(`/api/prayer/calendar?month=${ym(new Date())}`),
      api.get(`/api/prayer-wall/calendar?month=${ym(new Date())}`),
      prayerWallApi.list(),
      prayerWallApi.discover(),
      api.get('/api/prayer/cover-photos'),
      api.get('/api/prayer/stats'),
    ]);
    let events = [];
    if (cResult.status === 'fulfilled') { events = cResult.value.events || []; setCalEvents(events); }
    else setLoadError(t('prayer.failedToLoad'));
    if (wResult.status === 'fulfilled') { setWallCalEvents(wResult.value.events || []); }
    if (wallResult.status === 'fulfilled') { setWallItems(wallResult.value.items || []); setWallLoading(false); }
    if (discResult.status === 'fulfilled') { setDiscoverCount(discResult.value.total || 0); }
    if (coverResult.status === 'fulfilled') {
      setCoverPhotos({
        general:  coverResult.value.general  || [],
        memorial: coverResult.value.memorial || [],
      });
    }
    if (statsResult.status === 'fulfilled') {
      setPrayerStats({
        currentStreak: statsResult.value.currentStreak || 0,
        bestStreak:    statsResult.value.bestStreak    || 0,
      });
    }
    const recips = rResult.status === 'fulfilled' ? (rResult.value || []) : [];
    const todayStr = ymd(new Date());
    const hasEventsToday = events.some(e => e.scheduledFor.slice(0, 10) === todayStr);
    if (recips.length > 0 && !hasEventsToday) {
      try { const c = await api.post('/api/prayer/trigger-calendar'); setCalEvents(c.events || []); } catch {}
    }
    setLoading(false);
  }

  // ── Handlers ───────────────────────────────────────────────────

  async function refreshCalendar(month = ym(new Date())) {
    try {
      const c = await api.get(`/api/prayer/calendar?month=${month}`);
      setCalEvents(c.events || []);
      return c.events || [];
    } catch { return null; }
  }

  async function handlePray(recipientId, eventId) {
    try {
      await api.post(`/api/prayer/${recipientId}/pray`);
      const newEvents = await refreshCalendar() || [];
      if (eventId) {
        const updated = newEvents.find(e => e.id === eventId);
        if (updated) setSelectedEvent(updated);
      } else {
        navigate('/prayer/calendar', { replace: true });
        setViewDate(new Date()); setSelectedDay(ymd(new Date())); setSelectedEvent(null);
      }
      reloadRecipients();
    } catch {}
  }

  // Adding, editing or removing someone in People changes who the calendar
  // schedules, so re-pull events whenever the shared list mutates. This is why
  // the provider exposes a subscription rather than reaching into prayer state.
  useEffect(() => onRecipientsChanged(() => { refreshCalendar(ym(viewDate)); }),
    [onRecipientsChanged, viewDate]); // eslint-disable-line

  function handleNotesChange(eventId, notes) {
    setCalEvents(evts => evts.map(e => e.id === eventId ? { ...e, notes } : e));
    if (selectedEvent?.id === eventId) setSelectedEvent(ev => ({ ...ev, notes }));
    const ev = calEvents.find(e => e.id === eventId);
    // Event notes are the recipient's prayer request — mirror the edit into the
    // shared list so People shows it too, without a round trip.
    if (ev?.recipientId) {
      setRecipients(rs => rs.map(r => r.id === ev.recipientId ? { ...r, prayerRequest: notes } : r));
    }
  }

  function refreshStats() {
    api.get('/api/prayer/stats')
      .then(d => setPrayerStats({ currentStreak: d.currentStreak || 0, bestStreak: d.bestStreak || 0 }))
      .catch(() => {});
  }

  function handleEventPrayed(updatedEvent) {
    setCalEvents(evts => evts.map(e => e.id === updatedEvent.id ? updatedEvent : e));
    setSelectedEvent(updatedEvent);
    reloadRecipients();
    refreshStats();
    refreshDots();
    setTimeout(() => {
      setSelectedEvent(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 1200);
  }

  function handleEventUpdate(updatedEvent) {
    setCalEvents(evts => evts.map(e => e.id === updatedEvent.id ? { ...e, ...updatedEvent } : e));
    setSelectedEvent(prev => prev?.id === updatedEvent.id ? { ...prev, ...updatedEvent } : prev);
  }

  async function handleDeleteRecipient(id) {
    const removed = await deleteRecipient(id);
    if (removed) await refreshCalendar(ym(viewDate));
  }

  // Inviting someone to Ignite lives in RecipientsProvider — it is the same
  // action from the People list and from the calendar's event detail — and is
  // re-exported here as `handleInvite` so child screens keep their prop name.
  const handleInvite = invite;

  async function handlePrayOnDate(recipientId, dateStr) {
    try {
      await api.post(`/api/prayer/${recipientId}/pray-on-date`, { date: dateStr });
      const [y, m] = dateStr.split('-').map(Number);
      const targetYm = `${y}-${String(m).padStart(2, '0')}`;
      const c = await api.get(`/api/prayer/calendar?month=${targetYm}`);
      setCalEvents(prev => {
        const otherMonths = prev.filter(e => e.scheduledFor.slice(0, 7) !== targetYm);
        return [...otherMonths, ...(c.events || [])];
      });
    } catch (e) { alert(e.message || t('prayer.couldNotAdd')); }
  }

  async function ensureMonthsLoaded(months) {
    if (!months || months.length === 0) return;
    try {
      const results = await Promise.all(months.flatMap(m => [
        api.get(`/api/prayer/calendar?month=${m}`),
        api.get(`/api/prayer-wall/calendar?month=${m}`),
      ]));
      const newCal = [], newWall = [];
      for (let i = 0; i < months.length; i++) {
        newCal.push(...(results[i*2].events || []));
        newWall.push(...(results[i*2 + 1].events || []));
      }
      setCalEvents(prev => {
        const others = prev.filter(e => !months.includes(e.scheduledFor.slice(0, 7)));
        return [...others, ...newCal];
      });
      setWallCalEvents(prev => {
        const others = prev.filter(e => !months.includes(e.scheduledFor.slice(0, 7)));
        return [...others, ...newWall];
      });
    } catch {}
  }

  async function shiftRange(dir) {
    const newDate = viewMode === 'week'
      ? new Date(viewDate.getFullYear(), viewDate.getMonth(), viewDate.getDate() + dir * 7)
      : new Date(viewDate.getFullYear(), viewDate.getMonth() + dir, 1);
    setViewDate(newDate); setSelectedEvent(null);
    await ensureMonthsLoaded(visibleMonthsForRange(newDate, viewMode));
  }

  async function goToToday() {
    const today = new Date();
    setViewDate(today);
    setSelectedDay(ymd(today));
    setSelectedEvent(null);
    await ensureMonthsLoaded(visibleMonthsForRange(today, viewMode));
  }

  // When the user switches view mode, make sure any newly-visible months are loaded
  useEffect(() => {
    ensureMonthsLoaded(visibleMonthsForRange(viewDate, viewMode));
  }, [viewMode]); // eslint-disable-line

  function handleWallSaved(saved, isEdit) {
    setWallItems(prev => isEdit ? prev.map(i => i.id === saved.id ? saved : i) : [saved, ...prev]);
    navigate('/prayer/requests', { replace: true });
  }
  async function handleWallDelete(item) {
    if (!window.confirm('Delete this prayer request?')) return;
    try { await prayerWallApi.delete(item.id); setWallItems(prev => prev.filter(i => i.id !== item.id)); }
    catch (e) { alert(e.message || 'Could not delete.'); }
  }
  function handleWallUpdateAdded(itemId, update) {
    setWallItems(prev => prev.map(i => i.id === itemId ? { ...i, updates: [update, ...(i.updates || [])] } : i));
  }
  async function handleWallUpdateDeleted(itemId, updateId) {
    try {
      await prayerWallApi.deleteUpdate(updateId);
      setWallItems(prev => prev.map(i => i.id === itemId ? { ...i, updates: (i.updates || []).filter(u => u.id !== updateId) } : i));
    } catch (e) { alert(e.message || 'Could not delete update.'); }
  }

  // ── Derived state ──────────────────────────────────────────────
  const todayEvents     = calEvents.filter(e => e.scheduledFor.slice(0, 10) === ymd(new Date()));
  const wallEventsToday = wallCalEvents.filter(e => e.scheduledFor.slice(0, 10) === ymd(new Date()));
  const unprayedToday   = todayEvents.filter(e => !e.prayedAt).length + wallEventsToday.filter(e => !e.prayedAt).length;
  const totalToday      = todayEvents.length + wallEventsToday.length;
  const selectedDayEvts = calEvents.filter(e => e.scheduledFor.slice(0, 10) === selectedDay);
  const selectedWallEvts = wallCalEvents.filter(e => e.scheduledFor.slice(0, 10) === selectedDay);

  // ── Context passed to child screens ────────────────────────────
  const ctx = {
    // Data
    recipients, setRecipients, calEvents, setCalEvents,
    wallCalEvents, setWallCalEvents, wallItems, setWallItems,
    wallLoading, discoverCount, setDiscoverCount,
    deepLinkItemId, loading, loadError,
    // Calendar
    viewDate, setViewDate, selectedDay, setSelectedDay,
    selectedEvent, setSelectedEvent, detailRef,
    MONTHS, unprayedToday, totalToday, selectedDayEvts, selectedWallEvts,
    viewMode, setViewMode, shiftRange, goToToday,
    coverPhotos, prayerStats, refreshStats,
    // Handlers
    loadAll, openAdd, handlePray, handlePrayOnDate, handleInvite,
    handleNotesChange, handleEventPrayed, handleEventUpdate,
    handleDeleteRecipient, handleWallSaved, handleWallDelete,
    handleWallUpdateAdded, handleWallUpdateDeleted,
    setShowAddWall, setToast,
    // Wall edit/answer
    wallEditItem, setWallEditItem, wallAnswerItem, setWallAnswerItem,
    // Identity
    currentUserId, refreshDots, t,
  };

  // Calendar · Requests · Discover. Recipients is gone — the list moved to the
  // People section, and the calendar still schedules the same records.
  const tabs = [
    { to: '/prayer/calendar', label: t('prayer.calendarTab'),   Icon: CalendarDays,
      count: unprayedToday, highlight: true },
    { to: '/prayer/requests', label: t('prayer.requestsTitle'), Icon: BookMarked,
      count: wallItems.filter(i => i.user?.id === currentUserId).length },
    { to: '/prayer/discover', label: t('prayer.discoverTitle'), Icon: Compass,
      count: discoverCount, highlight: true },
  ];

  return (
    <div className="page">
      <PageHeader
        className="ph-no-line"
        title={t(`prayer.tabTitles.${activeTab}`)}
        subtitle={t(`prayer.tabSubtitles.${activeTab}`)}
        action={<PageGuide screenKey={`prayer.${activeTab}`} />}
      />

      <SectionTabs tabs={tabs} ariaLabel={t('nav.prayer')} />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><span className="spinner" style={{ width: 32, height: 32 }} /></div>
      ) : loadError ? (
        <div className="empty-state">
          <AlertTriangle size={36} strokeWidth={1.5} color="var(--text-lt)" />
          <h3>{t('common.somethingWrong')}</h3>
          <p>{loadError}</p>
          <button className="btn btn-primary" onClick={loadAll}>{t('prayer.tryAgain')}</button>
        </div>
      ) : (
        <Outlet context={ctx} />
      )}

      <div ref={addMenuRef}>
        {showAddMenu && (
          <div className="pr-add-menu pr-add-menu-fab">
            <button className="pr-add-menu-item" onClick={() => { setShowAddMenu(false); setShowAddWall(true); }}>
              <div className="pr-add-menu-icon" style={{ background: '#EEF2FF', color: '#6366F1' }}>
                <BookMarked size={15} />
              </div>
              <div>
                <div className="pr-add-menu-label">{t('prayer.addMenu.request')}</div>
                <div className="pr-add-menu-sub">{t('prayer.addMenu.requestSub')}</div>
              </div>
            </button>
            <div className="pr-add-menu-divider" />
            <button className="pr-add-menu-item" onClick={() => { setShowAddMenu(false); openAdd(); }}>
              <div className="pr-add-menu-icon" style={{ background: 'var(--teal-lt)', color: 'var(--teal)' }}>
                <UserPlus size={15} />
              </div>
              <div>
                <div className="pr-add-menu-label">{t('prayer.addMenu.person')}</div>
                <div className="pr-add-menu-sub">{t('prayer.addMenu.personSub')}</div>
              </div>
            </button>
            <button className="pr-add-menu-item" onClick={() => { setShowAddMenu(false); openQuickAdd(); }}>
              <div className="pr-add-menu-icon" style={{ background: '#FEF3C7', color: '#D97706' }}>
                <Users size={15} />
              </div>
              <div>
                <div className="pr-add-menu-label">{t('prayer.quickAddRecipients')}</div>
                <div className="pr-add-menu-sub">{t('prayer.addMultiplePeople')}</div>
              </div>
            </button>
          </div>
        )}
        <button className="fab" onClick={() => setShowAddMenu(o => !o)} aria-label={t('prayer.addBtn')}>
          <Plus size={24} strokeWidth={2.5} />
        </button>
      </div>

      {/* The recipient add/edit, quick-add and invite sheets are rendered by
          RecipientsProvider so Prayer and People share one set. */}
      {showAddWall && (
        <AddEditWallModal onClose={() => setShowAddWall(false)} onSave={(saved) => { handleWallSaved(saved, false); setShowAddWall(false); }} />
      )}
      {toast && <Toast message={toast} onHide={() => setToast('')} />}
    </div>
  );
}
